import React, { useState, useMemo, useEffect } from 'react';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RouteTableRow from './components/RouteTableRow';
import RouteCard from './components/RouteCard';
import RouteFormModal from './components/RouteFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import BulkActionsBar from './components/BulkActionsBar';
import FilterBar from './components/FilterBar';
import { routeService } from '../../services/routeService';

const RoutesManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'route_name', direction: 'asc' });
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [expandedCards, setExpandedCards] = useState([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deletingRoute, setDeletingRoute] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRoutes();
  }, []);

  const loadRoutes = async () => {
    try {
      setLoading(true);
      const data = await routeService?.getAll();
      setRoutes(data?.map(route => ({
        id: route?.id,
        route_name: route?.routeName,
        start_point: route?.startPoint,
        end_point: route?.endPoint,
        is_active: route?.isActive,
        departure_times: route?.departureTimes || [],
        last_modified: route?.lastModified
      })));
    } catch (err) {
      setError(err?.message || 'Rotalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedRoutes = useMemo(() => {
    let filtered = routes;

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(route =>
        route?.route_name?.toLowerCase()?.includes(query) ||
        route?.start_point?.toLowerCase()?.includes(query) ||
        route?.end_point?.toLowerCase()?.includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(route =>
        statusFilter === 'active' ? route?.is_active : !route?.is_active
      );
    }

    const sorted = [...filtered]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'last_modified') {
        aValue = new Date(aValue)?.getTime();
        bValue = new Date(bValue)?.getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [routes, searchQuery, statusFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectRoute = (id, checked) => {
    setSelectedRoutes(prev =>
      checked ? [...prev, id] : prev?.filter(routeId => routeId !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedRoutes(checked ? filteredAndSortedRoutes?.map(r => r?.id) : []);
  };

  const handleToggleExpand = (id) => {
    setExpandedCards(prev =>
      prev?.includes(id) ? prev?.filter(cardId => cardId !== id) : [...prev, id]
    );
  };

  const handleAddRoute = () => {
    setEditingRoute(null);
    setIsFormModalOpen(true);
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route);
    setIsFormModalOpen(true);
  };

  const handleDeleteRoute = (route) => {
    setDeletingRoute(route);
    setIsDeleteModalOpen(true);
  };

  const handleDuplicateRoute = async (route) => {
    try {
      await routeService?.create({
        routeName: `${route?.route_name} (Kopya)`,
        startPoint: route?.start_point,
        endPoint: route?.end_point,
        isActive: route?.is_active,
        departureTimes: route?.departure_times || []
      });
      await loadRoutes();
    } catch (err) {
      setError(err?.message || 'Rota kopyalanamadı');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const route = routes?.find(r => r?.id === id);
      if (route) {
        await routeService?.update(id, {
          routeName: route?.route_name,
          startPoint: route?.start_point,
          endPoint: route?.end_point,
          isActive: !route?.is_active,
          departureTimes: route?.departure_times || []
        });
        await loadRoutes();
      }
    } catch (err) {
      setError(err?.message || 'Durum güncellenemedi');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingRoute) {
        await routeService?.update(editingRoute?.id, {
          routeName: formData?.route_name,
          startPoint: formData?.start_point,
          endPoint: formData?.end_point,
          isActive: formData?.is_active,
          departureTimes: formData?.departure_times || []
        });
      } else {
        await routeService?.create({
          routeName: formData?.route_name,
          startPoint: formData?.start_point,
          endPoint: formData?.end_point,
          isActive: formData?.is_active,
          departureTimes: formData?.departure_times || []
        });
      }
      await loadRoutes();
      setIsFormModalOpen(false);
      setEditingRoute(null);
    } catch (err) {
      setError(err?.message || 'Rota kaydedilemedi');
    }
  };

  const handleConfirmDelete = async (id) => {
    try {
      await routeService?.delete(id);
      await loadRoutes();
      setSelectedRoutes(prev => prev?.filter(routeId => routeId !== id));
      setIsDeleteModalOpen(false);
      setDeletingRoute(null);
    } catch (err) {
      setError(err?.message || 'Rota silinemedi');
    }
  };

  const handleBulkActivate = async () => {
    try {
      await routeService?.bulkUpdateStatus(selectedRoutes, true);
      await loadRoutes();
      setSelectedRoutes([]);
    } catch (err) {
      setError(err?.message || 'Toplu işlem başarısız');
    }
  };

  const handleBulkDeactivate = async () => {
    try {
      await routeService?.bulkUpdateStatus(selectedRoutes, false);
      await loadRoutes();
      setSelectedRoutes([]);
    } catch (err) {
      setError(err?.message || 'Toplu işlem başarısız');
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`${selectedRoutes?.length} rotayı silmek istediğinizden emin misiniz?`)) {
      try {
        await routeService?.bulkDelete(selectedRoutes);
        await loadRoutes();
        setSelectedRoutes([]);
      } catch (err) {
        setError(err?.message || 'Toplu silme başarısız');
      }
    }
  };

  const handleBulkExport = () => {
    const selectedData = routes?.filter(route => selectedRoutes?.includes(route?.id));
    const dataStr = JSON.stringify(selectedData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rotalar_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} color="var(--color-muted-foreground)" />;
    }
    return (
      <Icon
        name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
        size={14}
        color="var(--color-primary)"
      />
    );
  };

  return (
    <div className="min-h-screen bg-background">
        <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userName="Ahmet Yılmaz"
          userRole="Sistem Yöneticisi"
        />

        <main
          className={`transition-smooth pt-4 pb-24 ${
            isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
          }`}
        >
          <div className="px-4 md:px-6 lg:px-8 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground font-heading mb-2">
                  Rota Yönetimi
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Dolmuş rotalarını ekleyin, düzenleyin ve yönetin
                </p>
              </div>
              <Button
                variant="default"
                size="default"
                onClick={handleAddRoute}
                iconName="Plus"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Yeni Rota Ekle
              </Button>
            </div>

            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              resultsCount={filteredAndSortedRoutes?.length}
            />

            <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          checked={
                            filteredAndSortedRoutes?.length > 0 &&
                            selectedRoutes?.length === filteredAndSortedRoutes?.length
                          }
                          onChange={(e) => handleSelectAll(e?.target?.checked)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                          aria-label="Tümünü seç"
                        />
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('route_name')}
                          className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                        >
                          Rota Adı
                          <SortIcon columnKey="route_name" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('start_point')}
                          className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                        >
                          Başlangıç
                          <SortIcon columnKey="start_point" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('end_point')}
                          className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                        >
                          Bitiş
                          <SortIcon columnKey="end_point" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="font-semibold text-sm text-foreground">Kalkış Saatleri</span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('is_active')}
                          className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                        >
                          Durum
                          <SortIcon columnKey="is_active" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('last_modified')}
                          className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-smooth"
                        >
                          Son Güncelleme
                          <SortIcon columnKey="last_modified" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left w-32">
                        <span className="font-semibold text-sm text-foreground">İşlemler</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedRoutes?.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full">
                              <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                            </div>
                            <p className="text-muted-foreground">Rota bulunamadı</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedRoutes?.map((route) => (
                        <RouteTableRow
                          key={route?.id}
                          route={route}
                          isSelected={selectedRoutes?.includes(route?.id)}
                          onSelect={handleSelectRoute}
                          onEdit={handleEditRoute}
                          onDelete={handleDeleteRoute}
                          onDuplicate={handleDuplicateRoute}
                          onToggleStatus={handleToggleStatus}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lg:hidden space-y-4">
              {filteredAndSortedRoutes?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full">
                      <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                    </div>
                    <p className="text-muted-foreground">Rota bulunamadı</p>
                  </div>
                </div>
              ) : (
                filteredAndSortedRoutes?.map((route) => (
                  <RouteCard
                    key={route?.id}
                    route={route}
                    isSelected={selectedRoutes?.includes(route?.id)}
                    onSelect={handleSelectRoute}
                    onEdit={handleEditRoute}
                    onDelete={handleDeleteRoute}
                    onDuplicate={handleDuplicateRoute}
                    onToggleStatus={handleToggleStatus}
                    isExpanded={expandedCards?.includes(route?.id)}
                    onToggleExpand={handleToggleExpand}
                  />
                ))
              )}
            </div>
          </div>
        </main>

        <BulkActionsBar
          selectedCount={selectedRoutes?.length}
          onActivate={handleBulkActivate}
          onDeactivate={handleBulkDeactivate}
          onDelete={handleBulkDelete}
          onExport={handleBulkExport}
          onClearSelection={() => setSelectedRoutes([])}
        />

        <RouteFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSubmit={handleFormSubmit}
          editRoute={editingRoute}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          route={deletingRoute}
          hasRelatedData={true}
        />
      </div>
  );
};

export default RoutesManagement;